/**
 * Square Web Payments SDK Utilities
 * 用于处理 Square 支付的工具函数
 */

// Square SDK 配置
export const SQUARE_CONFIG = {
  // 从环境变量获取配置
  applicationId: process.env.VUE_APP_ENV === 'production' ? process.env.VUE_APP_SQUARE_APP_ID : process.env.VUE_APP_TEST_SQUARE_APP_ID,
  locationId: process.env.VUE_APP_ENV === 'production' ? process.env['VUE_APP_SQUARE_DEFAULT_LOCATION_ID '] : process.env['VUE_APP_SQUARE_TEST_LOCATION_ID '],
  environment: process.env.VUE_APP_ENV === 'production' ? 'production' : 'sandbox', // 'sandbox' or 'production'
}

// 追踪当前加载的 SDK 环境
let currentLoadedEnvironment = null
let currentSquareScript = null

// 获取 Square SDK URL
export function getSquareSDKUrl(environment = SQUARE_CONFIG.environment) {
  return environment === 'production'
    ? 'https://web.squarecdn.com/v1/square.js'
    : 'https://sandbox.web.squarecdn.com/v1/square.js'
}

// 卸载当前的 Square SDK
export function unloadSquareSDK() {
  // 移除 script 标签
  if (currentSquareScript && currentSquareScript.parentNode) {
    currentSquareScript.parentNode.removeChild(currentSquareScript)
  }

  // 清理 window.Square 对象
  if (window.Square) {
    delete window.Square
  }

  // 重置追踪变量
  currentLoadedEnvironment = null
  currentSquareScript = null

  console.log('Square SDK unloaded')
}

// 动态加载 Square SDK
export function loadSquareSDK(forceEnvironment = null) {
  return new Promise((resolve, reject) => {
    console.log(process.env)
    const targetEnvironment = forceEnvironment || SQUARE_CONFIG.environment

    // 检查是否已加载正确环境的 SDK
    if (window.Square && currentLoadedEnvironment === targetEnvironment) {
      // console.log(`Square SDK already loaded for ${targetEnvironment} environment`)
      resolve(window.Square)
      return
    }

    // 如果加载了不同环境的 SDK，先卸载
    if (window.Square && currentLoadedEnvironment !== targetEnvironment) {
      console.log(`Switching Square SDK from ${currentLoadedEnvironment} to ${targetEnvironment}`)
      unloadSquareSDK()
    }

    const script = document.createElement('script')
    script.src = getSquareSDKUrl(targetEnvironment)
    script.async = true
    script.setAttribute('data-square-environment', targetEnvironment)

    script.onload = () => {
      if (window.Square) {
        currentLoadedEnvironment = targetEnvironment
        currentSquareScript = script
        console.log(`Square SDK loaded for ${targetEnvironment} environment`)
        resolve(window.Square)
      } else {
        reject(new Error('Square SDK failed to load'))
      }
    }

    script.onerror = () => {
      reject(new Error(`Failed to load Square SDK for ${targetEnvironment} environment`))
    }

    document.head.appendChild(script)
  })
}

// 获取当前加载的环境
export function getCurrentSquareEnvironment() {
  return currentLoadedEnvironment
}

// 切换 Square 环境
export async function switchSquareEnvironment(newEnvironment) {
  if (!['production', 'sandbox'].includes(newEnvironment)) {
    throw new Error('Invalid environment. Must be "production" or "sandbox"')
  }

  // 如果已经是目标环境，直接返回
  if (currentLoadedEnvironment === newEnvironment) {
    console.log(`Already in ${newEnvironment} environment`)
    return window.Square
  }

  // 加载新环境的 SDK
  return await loadSquareSDK(newEnvironment)
}

// 初始化 Square Payments
export async function initializeSquarePayments(productionEnvironment = false, locationId = null) {
  try {
    // 加载 SDK
    const env = SQUARE_CONFIG.environment === 'production' && productionEnvironment ? 'production' : 'sandbox'
    await loadSquareSDK(env)
    // 根据环境选择 location ID
    const appLocationId = env === 'production'
      ? (locationId || process.env.VUE_APP_SQUARE_DEFAULT_LOCATION_ID)
      : process.env.VUE_APP_SQUARE_TEST_LOCATION_ID

    console.log(SQUARE_CONFIG.applicationId, appLocationId)

    const payment = window.Square.payments(
      SQUARE_CONFIG.applicationId,
      appLocationId
    )

    // 初始化 payments 对象
    return { payment, appLocationId }
  } catch (error) {
    console.error('Failed to initialize Square payments:', error)
    throw error
  }
}

export async function squareBindType(payments, type, id, options = {}) {
  try {
    switch (type) {
      case 'card': {
        // Square Web SDK 会根据卡片发行国自动决定是否显示邮编字段
        // 美国、加拿大、英国的卡片必须输入邮编
        // 日本、中国的卡片不会显示邮编字段
        const card = await payments.card(options)
        if (card) {
          await card.attach('#' + id)
          return card
        }
        break
      }
      case 'cardOnFile': {
        // Charging a stored card with SCA (B2): Square's documented shape is a bare `payments.card()` whose
        // tokenize(verificationDetails, cardId) yields a fresh one-time token carrying the 3DS result. No
        // attach — there is nothing for the buyer to type — and it is kept separate from the attached
        // new-card instance so tokenize never reads that (empty) form.
        // docs/plans/2026-09-03-square-card-on-file-3ds-b2.md
        const cardOnFile = await payments.card(options)
        if (cardOnFile) {
          return cardOnFile
        }
        // Fail loud: a silently returned undefined would be cached by the caller and surface later as an
        // unhelpful "cannot read tokenize of undefined" instead of naming the real failure.
        throw new Error('Square card instance not created')
      }
      case 'applePay': {
        const applePay = await payments.applePay(options)
        if (applePay) {
          return applePay
        }
        break
      }
      case 'googlePay': {
        const googlePay = await payments.googlePay(options)
        if (googlePay) {
          return googlePay
        }
        break
      }
      default:
        break
    }
  } catch (error) {
    console.error(`Failed to bind Square ${type}:`, error)
    throw error
  }
}

export async function squareCreatePayment(payments, country, currency, total) {
  const currencyCode = currency ? currency.toUpperCase() : 'AUD'
  const countryCode = country ? country.toUpperCase() : 'AU'

  try {
    const paymentRequest = payments.paymentRequest({
      countryCode: countryCode,
      currencyCode: currencyCode,
      total: { amount: parseFloat(total).toString(), label: 'Total' },
    })
    console.log(paymentRequest)
    if (paymentRequest) {
      return paymentRequest
    }
  } catch (error) {
    console.error('Failed to create square payment:', error)
    throw error
  }
}

// Only non-empty strings make it into billingContact: Square treats every field it receives as a
// signal for 3DS, so an empty string is worse than an absent key.
function nonEmptyString(value) {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : ''
}

const SQUARE_VERIFY_INTENTS = ['CHARGE', 'CHARGE_AND_STORE']

/**
 * verificationDetails for Card.tokenize() (Square runs the risk evaluation / 3DS challenge inside
 * tokenize and the result rides on the returned token into CreatePayment).
 *
 * billingContact keys follow the Web Payments SDK `BillingContact` object: countryCode, givenName,
 * email, phone (…). `country` was the key of the deprecated verifyBuyer() docs and is ignored by
 * tokenize(). Give Square as much of the buyer as we already hold — it raises the chance that 3DS
 * verifies the cardholder without a challenge and lowers declines.
 * See docs/research/2026-09-02-square-web-payments-sca-3ds.md (F1).
 *
 * @param {object} [contact] the contactDetails store user: { name, email, phone }
 * @param {string} [intent] 'CHARGE_AND_STORE' (new card: charge, then the backend stores it from the
 *   payment id) or 'CHARGE' (stored card, B2: the card already exists, Square must not store it again).
 *   Anything else falls back to CHARGE_AND_STORE rather than sending a typo to Square.
 */
export function squareVerifyDetail(country, currency, total, contact, intent = 'CHARGE_AND_STORE') {
  const currencyCode = currency ? currency.toUpperCase() : 'AUD'
  const countryCode = country ? country.toUpperCase() : 'AU'
  const user = contact && typeof contact === 'object' ? contact : {}
  const verifyIntent = SQUARE_VERIFY_INTENTS.includes(intent) ? intent : 'CHARGE_AND_STORE'

  const billingContact = { countryCode }
  const givenName = nonEmptyString(user.name)
  if (givenName) billingContact.givenName = givenName
  const email = nonEmptyString(user.email)
  if (email) billingContact.email = email
  const phone = nonEmptyString(user.phone)
  if (phone) billingContact.phone = phone

  return {
    amount: parseFloat(total).toString(),
    billingContact,
    currencyCode: currencyCode,
    customerInitiated: true,
    sellerKeyedIn: false,
    intent: verifyIntent
  }
}

/**
 * Tokenize a payment method for CreatePayment. With `sourceId` (a `ccof:` card-on-file id, B2) the SDK
 * tokenizes the STORED card — Square runs the buyer verification / 3DS challenge inside this call and the
 * result rides on the returned one-time token, exactly like a newly typed card. Without it the call is the
 * unchanged new-card / wallet tokenize. A non-OK status (declined verification, buyer closed the challenge)
 * throws; callers decide what the customer sees.
 */
export async function squareHandleSubmit(payments, typeObject, type, verificationDetails, sourceId = null) {
  try {
    if (type === 'card' || type === 'applePay' || type === 'googlePay') {
      const result = sourceId
        ? await typeObject.tokenize(verificationDetails, sourceId)
        : await typeObject.tokenize(verificationDetails)
      if (result.status === 'OK') {
        // Never log the token itself: the console is captured by OpenReplay in production, which would upload a
        // live one-time payment credential with the session replay (B2 security review).
        console.log(`Square tokenize ${type}: ${result.status}`)
        // console.log(result)
        // Start the 3DS flow

        // const verificationResult = await payments.verifyBuyer(result.token, verificationDetails)
        // console.log(verificationResult)

        return result
      } else {
        let errorMessage = `Tokenization failed with status: ${result.status}`
        if (result.errors) {
          errorMessage += ` and errors: ${JSON.stringify(
            result.errors
          )}`
        }
        throw new Error(errorMessage)
      }
    }
  } catch (error) {
    console.error(`Failed to handle Square submit event ${type}:`, error)
    throw error
  }
}
